import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output, signal } from '@angular/core';
import { HttpError } from '../../../http/httpError';
import { UntypedFormControl, UntypedFormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { FormValidationErrors } from '@styleguide/form/form-validation-errors';
import { FormValidationErrorHandler } from '@styleguide/form/form-validation-error-handler.service';
import { equalityValidator } from '../../../form/validators/equality-validator';
import { AuthenticationService } from '../../authentication.service';
import { PasswordService } from '../../services/password.service';
import { BehaviorSubject, merge, Observable, Subject } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { LoadingDirective } from '@styleguide/loading/loading.directive';
import { AsyncPipe } from '@angular/common';
import { ErrorsComponent } from '@styleguide/error/errors/errors.component';
import { FormFieldComponent } from '@styleguide/form/form-field/form-field.component';
import { PasswordInputControlComponent } from '@styleguide/form/password-input-control/password-input-control.component';
import { IfInstitutionTypeDirective } from '../../../app-context/if-institution-type.directive';

const validationMessages = {
  token: {
    required: 'Bitte geben Sie einen gültigen Code ein',
  },
  password: {
    required: 'Bitte geben Sie ein Passwort ein',
  },
  passwordRepeated: {
    notEqual: 'Die beiden Passwörter stimmen nicht überein',
  },
};

@Component({
  selector: 'kgr-password-reset',
  templateUrl: './password-reset.component.html',
  styleUrls: ['./password-reset.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    LoadingDirective,
    ErrorsComponent,
    ReactiveFormsModule,
    FormFieldComponent,
    PasswordInputControlComponent,
    IfInstitutionTypeDirective,
    AsyncPipe,
  ],
})
export class PasswordResetComponent {
  @Output() passwordResetSuccessLoginToken = new EventEmitter<string>();
  @Input() token: string;

  loading$ = new BehaviorSubject<boolean>(false);
  done = false;
  apiError: HttpError;
  form: UntypedFormGroup;
  errors: string | string[];
  alive = true;
  username: string;

  validationErrors$!: Observable<FormValidationErrors>;
  private serverValidationErrors$ = new Subject<FormValidationErrors>();
  additionalErrorMessages: string[] = [];

  showErrorsIfPristine$ = new BehaviorSubject<boolean>(false);

  constructor(
    private formValidationErrorHandler: FormValidationErrorHandler,
    private authenticationService: AuthenticationService,
    private passwordService: PasswordService,
  ) {
    const passwordControl = new UntypedFormControl('', [Validators.required, Validators.minLength(3)]);
    const passwordRepeatControl = new UntypedFormControl('', [equalityValidator(passwordControl)]);
    passwordControl.valueChanges.pipe(takeUntilDestroyed()).subscribe(() => passwordRepeatControl.updateValueAndValidity());

    this.form = new UntypedFormGroup({ password: passwordControl, passwordRepeated: passwordRepeatControl });
    this.form.valueChanges.pipe(takeUntilDestroyed()).subscribe(() => this.resetErrors());

    this.validationErrors$ = merge(
      this.formValidationErrorHandler.getErrorsStream(this.form, validationMessages),
      this.serverValidationErrors$,
    );
  }

  private resetErrors() {
    this.errors = [];
    this.apiError = null;
  }

  onSubmit() {
    this.showErrorsIfPristine$.next(true);
    if (!this.form.valid) {
      this.resetErrors();
      return;
    }

    if (!this.loading$.value) {
      this.loading$.next(true);
      this.resetErrors();
      this.passwordService.reset(this.token, this.form.value['password']).subscribe({
        next: () => {
          this.authenticationService.logout().subscribe(() => {
            this.done = true;
            this.loading$.next(false);
            this.passwordResetSuccessLoginToken.next('TODO: retrieve login token from password reset api');
          });
        },
        error: (apiError: HttpError) => this.handleApiError(apiError),
      });
    }
  }

  protected handleApiError(error: HttpError) {
    this.apiError = error;
    if (error.code === 404) {
      this.errors = [
        $localize`Der übermittelte Code ist leider ungültig. Bitte kontrolliere Die Angaben mit dem Code aus deiner E-Mail. Hinweis: Jeder Code kann nur einmal verwendet werden.`,
      ];
    } else if (error.validationErrors) {
      this.serverValidationErrors$.next(error.validationErrors);
      this.additionalErrorMessages = this.formValidationErrorHandler.getUnmappedApiValidationErrorMessages(
        this.form,
        error.validationErrors,
      );
    } else {
      this.errors = [$localize`Es ist leider ein Fehler aufgetreten.`];
    }

    this.loading$.next(false);
  }
}
